import { UserType } from "@clovis/server/src/app/config/segment/types";
import LogoutIcon from "@heroicons/react/outline/LogoutIcon";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Undraw from "react-undraw";
import { useSegment } from "~/config/segment/SegmentProvider";
import { colors } from "~/config/stitches/colors";
import { useUser } from "~/config/user/UserProvider";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Column, Columns } from "~/design-system/Columns";
import { Heading } from "~/design-system/Heading";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { useLogout } from "~/screens/App/utils/useLogout/useLogout";
import { createComponentHook } from "~/types";

const useUnavailableScreen = createComponentHook(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { logout } = useLogout();
  const SegmentAnalyticsContext = useSegment();

  React.useEffect(() => {
    // Unavailable page should be accessible only if the user is locked
    if (!user.is_locked) {
      navigate("/", { replace: true });
    }
  }, []);

  React.useEffect(() => {
    if (SegmentAnalyticsContext) {
      // Track the event on Segment
      void SegmentAnalyticsContext.track({
        event: "Unavailable",
        properties: {
          organizationName: user.active_org?.org.name ?? "",
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          userEmail: user.contact!.email!,
          userType: user.active_org ? UserType.MEMBER : UserType.PARTICIPANT,
        },
        type: "track",
        userId: user.id,
      });
    }
  }, [SegmentAnalyticsContext, user]);

  const handleLogoutClick = () => {
    void logout();
  };

  return {
    actions: {
      handleLogoutClick,
    },
    state: {
      user,
    },
    t,
  };
});

function UnavailableScreen() {
  const { actions, state, t } = useUnavailableScreen();

  return (
    <Box
      display="flex"
      height="screen"
      width="screen"
      justifyContent="center"
      alignItems="center"
      padding="gutter"
    >
      <Columns space="large" width="auto" collapseBelow="tablet">
        <Column width="3-5" alignY="center">
          <Stack space="large">
            <Heading level="1">
              {t("screens.Unavailable.title", "Account unavailable")}
            </Heading>
            <Heading level="3">{state.user?.contact?.email}</Heading>
            <Text>
              {t(
                "screens.Unavailable.explanation",
                "To know more about it, contact our teams by using the button below or send us an email."
              )}{" "}
              👉 support@clovis.pro
            </Text>
            <Columns space="gutter">
              <Column width="content" alignY="center">
                <Button
                  size="large"
                  variant="solid"
                  tone="brandAccent"
                  dataClovisActionId="page-unavailable-button"
                  as={"a"}
                  href={"mailto:support@clovis.pro?subject=Contact-Locked"}
                >
                  {t("screens.Unavailable.contactUs", "Contact us")}
                </Button>
              </Column>
              <Column align="left" alignY="center">
                <Button
                  size="large"
                  variant="ghost"
                  trailingIcon={<LogoutIcon />}
                  onClick={actions.handleLogoutClick}
                  dataClovisActionId="unavailable-user-log-out"
                >
                  {t("screens.Unavailable.Header.logout", "Log out")}
                </Button>
              </Column>
            </Columns>
          </Stack>
        </Column>
        <Column alignY="center">
          <Undraw
            name="unavailable"
            primaryColor={colors.emerald500}
            height="100%"
          />
        </Column>
      </Columns>
    </Box>
  );
}

export { UnavailableScreen };
